/**
 * cms-ai-prompt-compiler.ts
 *
 * Fact-Grounded AI Image Prompt Compiler (Sprint CMS-STUDIO-THEME-MEDIA-03):
 * - Compiles creative visual prompts strictly grounded in merchant-approved facts.
 * - Strips prohibited unverified claims (e.g. false military radar or uncertified vault grades).
 * - Enforces requiresHumanReview and non-automatic publishing invariant.
 */

import type { SupportedLocale } from '../cms-contracts.js';

export interface CmsAiImagePromptRequest {
  readonly blockRole: 'hero' | 'pillar' | 'soc_diagram' | 'site_logo';
  readonly approvedFacts: readonly string[];
  readonly visualStyle: 'photorealistic' | 'illustration' | 'diagram' | 'abstract';
  readonly locale: SupportedLocale;
  readonly prohibitedClaims: readonly string[];
  readonly outputPurpose: 'draft_reference' | 'marketing_asset';
}

export interface CompiledAiPromptResult {
  readonly promptText: string;
  readonly approvedFactsIncluded: readonly string[];
  readonly prohibitedClaimsExcluded: readonly string[];
  readonly requiresHumanReview: true;
  readonly licensePolicy: 'tenant_owned' | 'licensed_stock' | 'ai_generated';
  readonly guidanceNotes: string;
}

export class CmsAiPromptCompiler {
  /**
   * Compiles creative visual prompts grounded strictly in merchant-approved facts.
   * Strips prohibited unverified claims (e.g. false military radar or uncertified vault grades).
   */
  public static compilePrompt(request: CmsAiImagePromptRequest): CompiledAiPromptResult {
    const excludedClaims: string[] = [];
    const sanitizedFacts = request.approvedFacts.filter((fact) => {
      const isProhibited = request.prohibitedClaims.some((prohibited) =>
        fact.toLowerCase().includes(prohibited.toLowerCase())
      );
      if (isProhibited) {
        excludedClaims.push(fact);
        return false;
      }
      return true;
    });

    let baseSubject = '';
    switch (request.blockRole) {
      case 'hero':
        baseSubject = 'High-end institutional asset defense command headquarters in Kampala, cinematic tactical lighting';
        break;
      case 'pillar':
        baseSubject = 'Sovereign asset protection vault and armored logistics convoy, operational security focus';
        break;
      case 'soc_diagram':
        baseSubject = 'Telemetry architecture schematic, edge AI video camera streams relaying to central operations centre';
        break;
      case 'site_logo':
        baseSubject = 'Minimalist geometric security emblem for institutional asset protection mesh';
        break;
    }

    const factsSnippet = sanitizedFacts.length > 0 ? ` featuring verified capabilities: ${sanitizedFacts.join('; ')}` : '';
    const styleSnippet = `Visual aesthetic: ${request.visualStyle}, professional grade, sharp focus, clean background, 8k resolution, zero watermark`;

    const fullPrompt = `${baseSubject}.${factsSnippet}. ${styleSnippet}.`.trim();

    return {
      promptText: fullPrompt,
      approvedFactsIncluded: sanitizedFacts,
      prohibitedClaimsExcluded: excludedClaims,
      requiresHumanReview: true,
      licensePolicy: 'ai_generated',
      guidanceNotes:
        'AI generation prompt is for creative drafting only. Generated output must be reviewed, licensed, and ingested into MediaAssetService before attaching to public CMS blocks.',
    };
  }
}
